// The difficulty ramp: a clock that walks the 30-row level table, and the two
// conditions that end a run. Ports `Cube.UpdateCurrentLevel` (`Cube.cs:3215`)
// and the level half of `BugsModel._cubeView_LevelChanged` (`:40-66`).
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

import type { EventBus } from "./events";
import { DIFFICULTIES, MIN_SPEED_FACTOR, type DifficultyDef, type LevelDef } from "./defs";

/**
 * What the spawning and movement systems need from the level system: the row
 * governing right now, and the walker speed multiplier it carries. `LevelClock`
 * implements it; `fixedLevel` pins it for tests that are not about levels.
 */
export interface LevelSource {
  readonly current: LevelDef;
  readonly speedFactor: number;
}

/** A `LevelSource` frozen on one row — the shape plan 06's tests want. */
export function fixedLevel(def: LevelDef): LevelSource {
  return { current: def, speedFactor: clampSpeedFactor(def.speedFactor) };
}

/** `BugsModel.cs:57`: a degenerate factor reads as 1 rather than freezing the game. */
function clampSpeedFactor(factor: number): number {
  return factor <= MIN_SPEED_FACTOR ? 1 : factor;
}

/**
 * The run's level counter.
 *
 * **Level 0 is a real state.** `_currentLevel` starts at 0 and `HandleInput`
 * returns early while it is below 1 (`Cube.cs:3190`), so the player cannot tap
 * anything for the first second. The table is indexed `max(0, level - 1)`
 * everywhere (`BugsModel.cs:1177`, `:1219`, `:1817`), so levels 0 and 1 both
 * read row 0 — the only difference is that level 0 eats input.
 *
 * **Each level begins one second after the number in the table.** The original
 * computes `ActiveAfterSeconds - (int)elapsed.TotalSeconds` and advances on
 * `< 0`, so the truncated elapsed time has to get *past* the row's number:
 * level 1 at t = 2 s (row says 1), level 2 at t = 31 s (row says 30). That is
 * one `>` versus `>=` and it is deterministic; "fixing" it would shift every
 * boundary in the port against the original by a second.
 */
export class LevelClock {
  private currentLevel = 0;
  private wonAt: number | null = null;

  constructor(
    private readonly events: EventBus,
    private readonly levels: readonly LevelDef[],
    // The difficulty crosses the menu -> game boundary as config (plan 17,
    // the original's static `GameConfiguration.DifficultyLevel`); it carries
    // the win level and a global speed multiplier. Defaulted to easy so every
    // pre-17 caller and test builds unchanged.
    private readonly difficulty: DifficultyDef = DIFFICULTIES[0],
  ) {}

  /** 0 before the first level begins; 1..levels.length after. */
  get level(): number {
    return this.currentLevel;
  }

  /** The row governing spawning right now — `levels[max(0, level - 1)]`. */
  get current(): LevelDef {
    return this.levels[Math.max(0, this.currentLevel - 1)];
  }

  /**
   * The current row's `SpeedFactor` (with the original's degenerate guard)
   * times the difficulty's `GlobalSpeedMultiplier`. Composed here — the one
   * place every reader (`updateBug`'s `maxSpeed`, the spawners) already fans
   * out from — so `fixedLevel()` test fixtures stay difficulty-blind.
   */
  get speedFactor(): number {
    return clampSpeedFactor(this.current.speedFactor) * this.difficulty.speedMultiplier;
  }

  /** Whether the player may act — the original's `_currentLevel < 1` gate. */
  get acceptsInput(): boolean {
    return this.currentLevel >= 1;
  }

  /**
   * Advance to `time` seconds of elapsed simulation, emitting one
   * `levelChanged` per level crossed. It loops rather than stepping once: a
   * suspended tab that releases a large accumulator must not swallow levels.
   *
   * The comparison reads the row the clock is *waiting for* (`levels[level]`,
   * not `[level - 1]`), which is what makes level 0 work without a special
   * case. The last level never rolls over — the original gives it a synthetic
   * `+30` window and then wins (`Cube.cs:3222`); here winning is this method's
   * job and the guard is simply `level < levels.length`.
   */
  update(time: number): void {
    const whole = Math.floor(time);
    while (this.currentLevel < this.levels.length) {
      if (whole <= this.levels[this.currentLevel].activeAfterSeconds) return;
      this.currentLevel++;
      // The win fires as the *winning* level's window expires — the original
      // checks `_currentLevel == gameWonAfterLevel` before incrementing
      // (`Cube.cs:3253`), so at difficulty 0 that is the crossing out of level
      // 10 at t = 301 s. Which level wins is the difficulty's `winAfterLevel`
      // (10/15/20 — plan 17). DIVERGE: the original `return`s into its win
      // sequence and stops advancing; this port's shell ignores the event in
      // `running` for now, so the clock keeps walking the table and the
      // remaining rows stay reachable.
      const winAfter = this.difficulty.winAfterLevel;
      if (this.currentLevel - 1 === winAfter && this.wonAt === null) {
        this.wonAt = winAfter;
        this.events.emit("gameWon", { level: winAfter });
      }
      this.events.emit("levelChanged", { level: this.currentLevel, def: this.current });
    }
  }
}
